import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Clock, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed | timeout
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      if (attemptsRef.current > 10) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const renderContent = () => {
    if (status === "paid") {
      return (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#D6B98D] mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-4" data-testid="success-title">
            Booking Confirmed
          </h1>
          <p className="text-[#A3A3A3] max-w-md mb-8">
            Thank you for choosing Overdrive Auto Spa. We've received your payment and a confirmation
            email is on its way. We'll see you at your scheduled appointment.
          </p>
        </>
      );
    }
    if (status === "expired" || status === "failed") {
      return (
        <>
          <XCircle className="w-16 h-16 text-red-400 mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-4" data-testid="failed-title">
            Payment Issue
          </h1>
          <p className="text-[#A3A3A3] max-w-md mb-8">
            Your payment session expired or could not be completed. Please try booking again.
          </p>
        </>
      );
    }
    if (status === "timeout") {
      return (
        <>
          <Clock className="w-16 h-16 text-[#D6B98D] mb-6" strokeWidth={1} />
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-4">
            Still Processing
          </h1>
          <p className="text-[#A3A3A3] max-w-md mb-8">
            Your payment is still processing. Please check your email for a confirmation.
          </p>
        </>
      );
    }
    return (
      <>
        <Clock className="w-16 h-16 text-[#D6B98D] mb-6 animate-pulse" strokeWidth={1} />
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight mb-4" data-testid="checking-title">
          Confirming Payment
        </h1>
        <p className="text-[#A3A3A3] max-w-md mb-8">
          Please hold while we verify your booking with Stripe…
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center px-6 text-center">
      {renderContent()}
      <Link
        to="/"
        data-testid="back-home-link"
        className="inline-flex items-center gap-2 px-6 py-3 border border-[#D6B98D] text-[#D6B98D] hover:bg-[#D6B98D] hover:text-[#050505] transition-all duration-300 rounded-sm tracking-wide text-sm uppercase"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Home
      </Link>
    </div>
  );
}
